import React, { useMemo } from 'react'
import { Menu } from 'antd'
import classNames from 'classnames'
import { NavLink } from 'react-router-dom'
import { useSelector } from 'react-redux'
import style from '../appNavMenu/style.module.scss'
import AppNavCategoryHeader from '../appNavCategoryHeader'

/**
 * Route segment to menu key mapping
 */
const ROUTE_TO_KEY_MAP = {
  instructions: 'agent-instructions',
  'knowledge-base': 'agent-knowledge-base',
  tool: 'agent-tool',
  'card-builder': 'agent-card-builder',
  variables: 'agent-variables',
  mcp: 'agent-mcp',
  deploy: 'agent-deploy',
  logs: 'agent-logs',
}

/**
 * Derives the selected menu key from the current route path.
 * Matches the last significant segment of the agent config path.
 *
 * @param {string} pathname - Current route pathname
 * @returns {string|null} The menu key or null if no match
 */
const getSelectedKeyFromPath = pathname => {
  if (!pathname) return null

  // Match agent config routes: /app/:appID/ai-agents/:agentUid/:section/...
  const agentConfigMatch = pathname.match(/\/ai-agents\/[^/]+\/([^/]+)/)
  if (agentConfigMatch) {
    const segment = agentConfigMatch[1]
    return ROUTE_TO_KEY_MAP[segment] || null
  }

  return null
}

const AgentConfigMenu = ({
  activeTab,
  appID,
  agentUid,
  openKeys,
  onOpenChange,
  onSelect,
  selectedKeys: selectedKeysProp,
}) => {
  const apps = useSelector(state => state.apps)
  const { activeBuilderAgent } = apps

  // Use the active builder agent UID if available, fallback to agentUid prop
  const currentAgentUid = activeBuilderAgent?.uid || agentUid

  // Derive selectedKeys from activeTab (current path) if not provided via prop
  const selectedKeys = useMemo(() => {
    if (selectedKeysProp && selectedKeysProp.length > 0) {
      return selectedKeysProp
    }
    const derivedKey = getSelectedKeyFromPath(activeTab)
    return derivedKey ? [derivedKey] : []
  }, [selectedKeysProp, activeTab])

  // Menu items configuration
  const menuItems = useMemo(
    () => [
      {
        key: 'agent-instructions',
        label: 'Instructions',
        iconClass: style.appNavMenuIconInstructions,
        path: `/app/${appID}/ai-agents/${currentAgentUid}/instructions`,
      },
      {
        key: 'agent-knowledge-base',
        label: 'Knowledge Base',
        iconClass: style.appNavMenuIconKnowledgeBase,
        path: `/app/${appID}/ai-agents/${currentAgentUid}/knowledge-base`,
      },
      {
        key: 'agent-tool',
        label: 'Tools',
        iconClass: style.appNavMenuIconTool,
        path: `/app/${appID}/ai-agents/${currentAgentUid}/tool`,
      },
      {
        key: 'agent-card-builder',
        label: 'Card Builder',
        iconClass: style.appNavMenuIconCardBuilder,
        path: `/app/${appID}/ai-agents/${currentAgentUid}/card-builder`,
        external: true,
      },
      {
        key: 'agent-variables',
        label: 'Variables',
        iconClass: style.appNavMenuIconVariables,
        path: `/app/${appID}/ai-agents/${currentAgentUid}/variables`,
      },
      {
        key: 'agent-mcp',
        label: 'MCP',
        iconClass: style.appNavMenuIconMCPConfig,
        path: `/app/${appID}/ai-agents/${currentAgentUid}/mcp`,
      },
      {
        key: 'agent-deploy',
        label: 'Deploy',
        iconClass: style.appNavMenuIconDeploy,
        path: `/app/${appID}/ai-agents/${currentAgentUid}/deploy`,
      },
      {
        key: 'agent-logs',
        label: 'Logs',
        iconClass: style.appNavMenuIconLogs || '',
        path: `/app/${appID}/ai-agents/${currentAgentUid}/logs`,
      },
    ],
    [appID, currentAgentUid],
  )

  return (
    <>
      <AppNavCategoryHeader
        name={activeBuilderAgent?.name && activeBuilderAgent.name.toUpperCase()}
      />
      <Menu
        className={style.appNavMenuCustom}
        mode="inline"
        openKeys={openKeys}
        onOpenChange={onOpenChange}
        onSelect={onSelect}
        selectedKeys={selectedKeys}
      >
        {menuItems.map(item => (
          <Menu.Item key={item.key} className={style.appNavMenuCustomItem}>
            {item.external ? (
              <a href={item.path} target="_blank" rel="noreferrer" style={{ marginBottom: '0px' }}>
                <div className={style.appNavLinkWrapper}>
                  <div
                    className={classNames(
                      style.appNavMenuIcon,
                      item.iconClass,
                      '__menu__item__icon',
                    )}
                  />
                  <span>
                    {item.label} <i className="fe fe-external-link" />
                  </span>
                </div>
              </a>
            ) : (
              <NavLink to={item.path} style={{ marginBottom: '0px' }} exact>
                <div className={style.appNavLinkWrapper}>
                  <div
                    className={classNames(
                      style.appNavMenuIcon,
                      item.iconClass,
                      '__menu__item__icon',
                    )}
                  />
                  <span>{item.label}</span>
                </div>
              </NavLink>
            )}
          </Menu.Item>
        ))}
      </Menu>
    </>
  )
}

export default AgentConfigMenu
